import { useMutation } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { FaArrowRotateLeft } from 'react-icons/fa6'

import { useDialogBox } from '../hooks/useDialogBox'
import { queryClient } from '../libs/reactQuery'
import { cn } from '../libs/tailwindMerge'
import { PendingUpload } from '../utils/indexedDB/addPendingUpload'
import { getPendingUploads } from '../utils/indexedDB/getPendingUploads'
import { retryPendingUpload } from '../utils/indexedDB/retryPendingUpload'

interface UploadResultStatus {
  done: boolean
  error?: Error
  message?: string
}

interface NotificationPendingUploadProps {
  handleSetIsRetryingUpload: (value: boolean) => void
}

export function NotificationPendingUpload({ handleSetIsRetryingUpload }: NotificationPendingUploadProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [uploadingMessage, setUploadingMessage] = useState('---')
  const [pendingUploads, setPendingUploads] = useState<PendingUpload[] | null>(null)
  const [uploadResultStatus, setUploadResultStatus] = useState<UploadResultStatus[] | []>([])

  const { showDialog } = useDialogBox()

  async function handleRetry() {
    if (isSubmitting) return
    setIsSubmitting(true)
    handleSetIsRetryingUpload(true)
    handlePendingUploadRetry(undefined, {
      onSettled: () => {
        setIsSubmitting(false)
        handleSetIsRetryingUpload(false)
        setUploadingMessage('---')
      },
      onError: (error) => {
        console.log(error)
      },
    })
  }

  const { mutate: handlePendingUploadRetry } = useMutation({
    mutationFn: async () => {
      const result = await retryPendingUpload((message: string) => {
        setUploadingMessage(message)
      })

      if (result.every(item => item.done)) {
        setUploadResultStatus([])
        return showDialog({
          title: 'Uploads concluídos 👍',
          type: 'success',
        })
      } else if (!result.every(item => item.done) && !result.every(item => !item.done)) {
        setUploadResultStatus(result.map(item => ({
          done: item.done,
          message: item.done
            ? 'Concluído'
            : 'Falhou',
          error: item.error,
        })))
        return showDialog({
          title: 'Alguns uploads falharam',
          type: 'warning',
        })
      }
      setUploadResultStatus(result.map(item => ({
        done: item.done,
        message: 'Falhou',
        error: item.error,
      })))
      throw new Error('Todos as tentativas uploads falharam')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['reports', 'report_detail'],
      })
    },
    onError: (error) => {
      showDialog({
        title: error.message,
        description: 'Tente novamente mais tarde',
      })
    },
  })

  useEffect(() => {
    const pending = getPendingUploads() as unknown as PendingUpload[]
    if (pending) {
      setPendingUploads(pending)
    }
  }, [])

  if (!pendingUploads) return <></>

  return (
    <div
      className="grid grid-cols-[1fr_2.25rem] border-b last:border-none"
    >
      <div>
        <div className="bg-white hover:brightness-95 p-2 min-h-16">
          <div className="flex items-center justify-between">
            <div className="flex space-x-2">
              <div className="flex flex-col mb-2">
                <strong className="leading-tight text-nowrap text-[12px] sm:text-sm">
                  Você tem {pendingUploads.length} upload{pendingUploads.length > 1 && 's'} pendente{pendingUploads.length > 1 && 's'}
                </strong>
                <span
                  className={cn(
                    'text-nowrap text-[12px] sm:text-sm text-white',
                    isSubmitting && 'text-slate-400',
                  )}
                >
                  {uploadingMessage}
                </span>
              </div>
            </div>
          </div>
          <p className="text-[10px] sm:text-xs w-[15.75rem] sm:w-[30rem]">Clique no botão à direita para tentar enviar novamente</p>
          {uploadResultStatus.length > 0 && uploadResultStatus.map((item, i) => (
            <details key={i}>
              <summary className="text-[10px] sm:text-xs w-[15.75rem] sm:w-[30rem] mt-4 cursor-pointer list-none font-semibold">
                {Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' }).format(new Date(pendingUploads[i].created_at))}
                <span className="font-semibold text-red-500">{' '} {item.message}</span>
              </summary>
              <div className="flex flex-col mt-2">
                <span
                  className={cn(
                    'text-[10px] sm:text-xs w-[15.75rem] sm:w-[30rem]',
                    item.done
                      ? 'text-green-600'
                      : 'text-red-600',
                  )}
                >
                  {item.error && item.error.message}
                </span>
              </div>
            </details>
          ))}
        </div>
      </div>
      <button
        type="button"
        className={cn(
          'flex flex-1 items-center justify-center px-3 text-gray-50 bg-indigo-300 disabled:cursor-not-allowed',
        )}
        title="Marcar como visto"
        disabled={isSubmitting}
        onClick={() => handleRetry()}
      >
        <div>
          <FaArrowRotateLeft
            className={cn(
              isSubmitting && 'animate-reverse-spin',
            )}
            size={15}
          />
        </div>
      </button>
    </div>
    // <p>{JSON.stringify(pendingUploads)}</p>
  )
}
